import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

/**
 * Org / team context store.
 *
 * `activeTeamId` is the team whose data the user is currently viewing.
 *   - `null` = "All Teams" (aggregates data from every team the user belongs to
 *     plus org-wide entities with `team_id IS NULL`).
 *   - a number = view is scoped to that team; the client sends `X-Team-Id: <id>`
 *     on every API request (see `client/src/services/api/client.ts`).
 *
 * Persisted under the `org-storage` localStorage key so selection survives
 * reloads. The API client reads this key directly to inject the header.
 *
 * QG-CLI-COMP-003: because it is persisted and read straight out of localStorage, a team id
 * left over from a PREVIOUS identity keeps being sent as `X-Team-Id` after an account switch
 * or a re-login. The server rejects a team the current user does not belong to
 * (server/src/api/middleware/policy.ts), so every policied route 403s until the user happens
 * to reselect a team. Only TeamSelector ever wrote this value; nothing cleared it.
 * The auth store now calls `clearTeamContext()` on login and logout.
 */
export interface OrgState {
  activeTeamId: number | null;
  setActiveTeam: (teamId: number | null) => void;
  /**
   * Reset team scoping. MUST be called whenever the identity behind the session changes
   * (login, logout, account switch) — see QG-CLI-COMP-003.
   */
  clearTeamContext: () => void;
}

export const useOrgStore = create<OrgState>()(
  devtools(
    persist(
      (set) => ({
        activeTeamId: null,
        setActiveTeam: (teamId) => set({ activeTeamId: teamId }),
        clearTeamContext: () => set({ activeTeamId: null }),
      }),
      { name: 'org-storage', storage: createJSONStorage(() => localStorage) },
    ),
  ),
);
